/**
 * TCGdex Pricing Utility
 * Fetches market pricing from TCGdex API (CardMarket + TCGPlayer).
 * TCGdex provides market-average pricing (~NM equivalent), not per-condition.
 */

const EUR_TO_USD = 1.10;

/**
 * Fetch pricing data from TCGdex for a specific card.
 * @param {string} tcgdexCardId - TCGdex card ID (e.g., "base1-4", "sv04-197")
 * @returns {Object|null} Normalized pricing object or null if unavailable
 */
export async function fetchTCGdexPricing(tcgdexCardId) {
  if (!tcgdexCardId) return null;

  try {
    const res = await fetch(`https://api.tcgdex.net/v2/en/cards/${tcgdexCardId}`);
    if (!res.ok) return null;

    const data = await res.json();
    if (!data.pricing) return null;

    const cm = data.pricing.cardmarket || null;
    const tcg = data.pricing.tcgplayer || null;

    if (!cm && !tcg) return null;

    const result = {
      cardmarket: null,
      tcgplayer: null,
      fetchedAt: new Date().toISOString(),
    };

    if (cm) {
      result.cardmarket = {
        currency: 'EUR',
        updated: cm.updated || null,
        avg: cm.avg || null,
        avgUSD: cm.avg ? +(cm.avg * EUR_TO_USD).toFixed(2) : null,
        low: cm.low || null,
        lowUSD: cm.low ? +(cm.low * EUR_TO_USD).toFixed(2) : null,
        trend: cm.trend || null,
        trendUSD: cm.trend ? +(cm.trend * EUR_TO_USD).toFixed(2) : null,
        avg1: cm.avg1 || null,
        avg7: cm.avg7 || null,
        avg30: cm.avg30 || null,
        holoAvg: cm['avg-holo'] || null,
        holoAvgUSD: cm['avg-holo'] ? +(cm['avg-holo'] * EUR_TO_USD).toFixed(2) : null,
        holoLow: cm['low-holo'] || null,
        holoTrend: cm['trend-holo'] || null,
        holoAvg1: cm['avg1-holo'] || null,
        holoAvg7: cm['avg7-holo'] || null,
        holoAvg30: cm['avg30-holo'] || null,
      };
    }

    if (tcg) {
      const extractVariant = (v) => {
        if (!v) return null;
        return {
          low: v.lowPrice || null,
          mid: v.midPrice || null,
          high: v.highPrice || null,
          market: v.marketPrice || null,
          directLow: v.directLowPrice || null,
        };
      };

      result.tcgplayer = {
        currency: 'USD',
        updated: tcg.updated || null,
        normal: extractVariant(tcg.normal),
        holofoil: extractVariant(tcg.holofoil),
        reverseHolofoil: extractVariant(tcg.reverseHolofoil || tcg['reverse-holofoil']),
        firstEdition: extractVariant(tcg['1stEditionHolofoil'] || tcg.firstEdition),
      };
    }

    return result;
  } catch (err) {
    console.error('[TCGdex] Pricing fetch error:', err.message);
    return null;
  }
}

/**
 * Get the best single market price from TCGdex data.
 * Prefers TCGPlayer market price, falls back to CardMarket avg.
 * @param {Object} tcgdexData - Output from fetchTCGdexPricing
 * @param {string} variant - Card variant (e.g., "Holo", "Normal", "Reverse Holo")
 * @returns {number|null} Best price in USD
 */
export function getTCGdexBestPrice(tcgdexData, variant = 'Normal') {
  if (!tcgdexData) return null;

  const tcg = tcgdexData.tcgplayer;
  if (tcg) {
    const variantKey = (variant || 'Normal').toLowerCase().replace(/\s/g, '');
    const variantMap = {
      normal: tcg.normal,
      holo: tcg.holofoil,
      reverseholo: tcg.reverseHolofoil,
      '1stedition': tcg.firstEdition,
    };
    const match = variantMap[variantKey] || tcg.holofoil || tcg.normal;
    if (match?.market) return match.market;
    if (match?.mid) return match.mid;
  }

  const cm = tcgdexData.cardmarket;
  if (cm) {
    const isHolo = /holo|reverse|1st/i.test(variant);
    if (isHolo && cm.holoAvgUSD) return cm.holoAvgUSD;
    if (cm.avgUSD) return cm.avgUSD;
  }

  return null;
}
